import React, { useRef, useCallback, useState, useEffect } from 'react';
import studentProfile from '../data/StudentProfile.json';
import { ARCHETYPES } from '../config/archetypes';

// ─── Step definitions (6-mark / 9-mark) ──────────────────────────────────────

const SIX_MARK_STEPS = [
  {
    id: 'summary',
    label: 'Step 1 of 2',
    title: 'Summary of Process',
    placeholder:
      'Explain the key process or concept. Use correct subject terminology.',
    rows: 3,
  },
  {
    id: 'point1',
    label: 'Step 2 of 2',
    title: 'Study / Example Application',
    placeholder:
      'Apply a specific study or example to the question. Include specific named data and statistics.',
    rows: 5,
  },
];

const NINE_MARK_STEPS = [
  {
    id: 'summary',
    label: 'Step 1 of 4',
    title: 'Summary Position',
    placeholder:
      'State your overall evaluative argument. What is your main judgement on the question?',
    rows: 3,
  },
  {
    id: 'point1',
    label: 'Step 2 of 4',
    title: 'Key Study or Example (AO1)',
    placeholder:
      'Develop your argument using a named study or example with specific data.',
    rows: 5,
  },
  {
    id: 'point2',
    label: 'Step 3 of 4',
    title: 'Brief Example for Breadth (AO2)',
    placeholder:
      'Add breadth with a contrasting brief example. Show connections across the topic.',
    rows: 5,
  },
  {
    id: 'conclusion',
    label: 'Step 4 of 4',
    title: 'Evaluative Conclusion',
    placeholder:
      'Make a final evaluative judgement. Assess the extent and refer back to the question.',
    rows: 3,
  },
];

const STEP_COLORS = [
  { border: 'border-[#58a6ff]', label: 'text-[#58a6ff]', badge: 'bg-[#58a6ff]/10 text-[#58a6ff]' },
  { border: 'border-[#3fb950]', label: 'text-[#3fb950]', badge: 'bg-[#3fb950]/10 text-[#3fb950]' },
  { border: 'border-[#d29922]', label: 'text-[#d29922]', badge: 'bg-[#d29922]/10 text-[#d29922]' },
  { border: 'border-[#bc8cff]', label: 'text-[#bc8cff]', badge: 'bg-[#bc8cff]/10 text-[#bc8cff]' },
];


function wordCount(text) {
  if (!text?.trim()) return 0;
  return text.trim().split(/\s+/).length;
}

function formatTime(s) {
  const m   = Math.floor(s / 60).toString().padStart(2, '0');
  const sec = (s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

// ─── MCQ Card ─────────────────────────────────────────────────────────────────

function McqCard({ question, onMcqAnswer }) {
  const options      = question.options ?? [];
  const maxSelect    = question.marks ?? 1;
  const [selected, setSelected] = useState([]);
  const isRevealed   = selected.length >= maxSelect;

  const handleSelect = (label) => {
    if (isRevealed) return;
    setSelected((prev) => {
      if (prev.includes(label)) return prev.filter((l) => l !== label);
      const next = [...prev, label];
      if (next.length === maxSelect) {
        const marksAwarded = options.filter(
          (o) => o.is_correct && next.includes(o.label)
        ).length;
        onMcqAnswer?.({
          selected: next,
          options,
          marksAwarded,
          marksTotal: maxSelect,
        });
      }
      return next;
    });
  };

  const stem = question.question_text ?? '';
  const year     = question.year;
  const qNum     = question.question_number;
  const topic    = question.topic?.replace(/_/g, ' ');

  return (
    <div className="flex-1 flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="px-6 pt-4 pb-3 bg-[#0d1117] sticky top-0 z-10 border-b border-[#30363d]"
           style={{ boxShadow: '0 4px 12px 0 rgba(13,17,23,0.85)' }}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs px-2 py-0.5 rounded-full border border-[#30363d] text-[#8b949e] capitalize">{topic}</span>
          {year && <span className="text-xs text-[#8b949e] opacity-60">{year}</span>}
          {qNum && <span className="text-xs text-[#484f58] font-mono">Q{qNum}</span>}
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-400">
            {maxSelect === 1 ? '1 mark' : `${maxSelect} marks`}
          </span>
        </div>
        <p className="text-[#e6edf3] text-sm font-medium leading-relaxed whitespace-pre-line">{stem}</p>
      </div>

      {/* Options */}
      <div className="flex-1 px-6 py-4 space-y-2">
        {options.map((opt) => {
          const wasSelected = selected.includes(opt.label);
          let state = 'idle';
          if (isRevealed) {
            if (wasSelected) state = opt.is_correct ? 'correct' : 'wrong';
            else if (opt.is_correct) state = 'missed';
            else state = 'dimmed';
          } else if (wasSelected) {
            state = 'pending';
          }

          const styles = {
            idle:    'border-[#30363d] text-[#e6edf3] hover:border-[#58a6ff]/40 hover:bg-[#58a6ff]/5 cursor-pointer',
            pending: 'border-[#58a6ff]/60 bg-[#58a6ff]/10 text-[#58a6ff] cursor-pointer',
            correct: 'border-[#3fb950] bg-[#3fb950]/10 text-[#3fb950] cursor-default',
            wrong:   'border-[#f85149] bg-[#f85149]/10 text-[#f85149] cursor-default',
            missed:  'border-[#d29922]/60 bg-[#d29922]/8 text-[#d29922] cursor-default',
            dimmed:  'border-[#21262d] text-[#484f58] cursor-default',
          };

          const labelStyles = {
            idle:    'border-[#30363d] text-[#8b949e]',
            pending: 'border-[#58a6ff]/60 text-[#58a6ff]',
            correct: 'border-[#3fb950]/60 text-[#3fb950]',
            wrong:   'border-[#f85149]/60 text-[#f85149]',
            missed:  'border-[#d29922]/60 text-[#d29922]',
            dimmed:  'border-[#21262d] text-[#484f58]',
          };

          return (
            <button
              key={opt.label}
              onClick={() => handleSelect(opt.label)}
              disabled={isRevealed}
              className={`w-full text-left px-4 py-3 rounded-lg border text-sm leading-snug transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#58a6ff]/60 ${styles[state]}`}
            >
              <div className="flex items-start gap-3">
                <span className={`flex-shrink-0 w-5 h-5 rounded border text-[10px] font-mono font-semibold flex items-center justify-center mt-0.5 ${labelStyles[state]}`}>
                  {opt.label}
                </span>
                <span>{opt.text}</span>
              </div>
            </button>
          );
        })}
      </div>

      {!isRevealed && maxSelect > 1 && (
        <p className="px-6 pb-4 text-xs text-[#484f58] text-center">
          Select {maxSelect - selected.length} more answer{maxSelect - selected.length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

export default function AnswerPanel({
  question,
  steps,
  onChange,
  onSubmit,
  onClearAll,
  isLoading,
  hasBeenMarked,
  markType,
  archetype,
  timerElapsed,
  timerActive,
  guidedMode,
  onMcqAnswer,
}) {
  const refs          = useRef([]);
  const containerRef  = useRef(null);

  const [revealedHints, setRevealedHints]               = useState({});
  const [revealedModelAnswers, setRevealedModelAnswers] = useState(new Set());
  const [animatingSteps, setAnimatingSteps]             = useState(new Set());
  const [breadthIndex, setBreadthIndex]                 = useState(0);
  const [showClearConfirm, setShowClearConfirm]         = useState(false);

  // Derive active step set from markType (6/9 mark path)
  const activeSteps   = markType === '6-mark' ? SIX_MARK_STEPS : NINE_MARK_STEPS;

  // Reset all local state on question change OR archetype change
  useEffect(() => {
    setRevealedHints({});
    setRevealedModelAnswers(new Set());
    setAnimatingSteps(new Set());
    setBreadthIndex(0);
    containerRef.current?.scrollTo({ top: 0 });
  }, [question?.id, markType, archetype]);

  // Reset when steps are externally cleared
  useEffect(() => {
    const isEmpty = Object.values(steps).every((v) => !v?.trim());
    if (isEmpty) {
      setRevealedHints({});
      setRevealedModelAnswers(new Set());
      setBreadthIndex(0);
    }
  }, [steps]);

  const handleRevealHint = useCallback((stepId, type) => {
    setRevealedHints((prev) => ({
      ...prev,
      [stepId]: { ...prev[stepId], [type]: true },
    }));
  }, []);

  const handleRunModelAnswer = useCallback(
    (stepId) => {
      // modelAnswer keyed by step id for 6/9 mark questions
      const modelText = question?.modelAnswer?.[stepId];
      if (!modelText) return;
      onChange(stepId, modelText);
      setRevealedModelAnswers((prev) => new Set([...prev, stepId]));
      setAnimatingSteps((prev) => new Set([...prev, stepId]));
      setTimeout(() => {
        setAnimatingSteps((prev) => {
          const next = new Set(prev);
          next.delete(stepId);
          return next;
        });
      }, 700);
    },
    [question, onChange]
  );

  const handleKeyDown = useCallback(
    (e, index, total) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        const next = e.shiftKey ? index - 1 : index + 1;
        if (next >= 0 && next < total) {
          refs.current[next]?.focus();
        } else if (next === total && !e.shiftKey) {
          onSubmit();
        }
      }
    },
    [onSubmit]
  );

  // ─── Shared question field helpers (handles both old & new data shapes) ──
  const questionText   = question?.question_text ?? question?.question ?? '';
  const majorCS        = question?.major_case_study ?? question?.majorCaseStudy ?? '';
  const csHint         = question?.caseStudyHint ?? question?.major_case_study ?? '';
  const briefExamples  = question?.briefExamples ?? question?.brief_examples ?? [];

  const vaultFact = question
    ? studentProfile.knowledgeVault?.[majorCS]?.fact ?? null
    : null;

  // ─── Empty state ──────────────────────────────────────────────────────────
  if (!question) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="text-5xl opacity-20">←</div>
          <p className="text-[#8b949e] text-sm">Select a question from the sidebar to begin</p>
          <p className="text-[#8b949e] text-xs opacity-60">Questions across multiple topics</p>
        </div>
      </div>
    );
  }

  // ─── MCQ LAYOUT (questions with options array) ────────────────────────────
  if (Array.isArray(question.options) && question.options.length > 0) {
    return <McqCard question={question} onMcqAnswer={onMcqAnswer} />;
  }

  // ─── OUTLINE_4 / DATA_6 / ASSESS_6 / ENVSCI_SHORT / GCSE SCIENCE LAYOUT ─────
  if (
    archetype === 'outline_4' ||
    archetype === 'data_6' ||
    archetype === 'assess_6' ||
    archetype === 'envsci_short' ||
    archetype === 'gcse_science_answer' ||
    archetype === 'gcse_science_extended_6'
  ) {
    const archetypeDef  = ARCHETYPES[archetype];
    const allCards      = archetypeDef.cards;
    const keyFacts      = question.key_facts ?? [];

    // For envsci_short, show fewer cards depending on marks:
    //   1 mark → 1 card, 2–3 marks → 2 cards, 4–5 marks → all 4 cards.
    const visibleCount = archetype === 'envsci_short'
      ? ((question.marks ?? 1) <= 1 ? 1 : (question.marks ?? 1) <= 3 ? 2 : allCards.length)
      : allCards.length;

    const cards      = allCards.slice(0, visibleCount);
    const cardLabels = cards.map((c) => c.label);

    const allCardsFilled  = cardLabels.every((lbl) => steps[lbl]?.trim().length > 0);

    // Count filled cards + total words for header
    const filledCount = cardLabels.filter((lbl) => steps[lbl]?.trim()).length;
    const totalWords  = cardLabels.reduce((acc, lbl) => acc + wordCount(steps[lbl]), 0);

    // Pre-compute fact per card: POINT cards get keyFacts[0] / [1] (fallback to vault)
    const cardFacts = [];
    let pIdx = 0;
    for (let i = 0; i < cards.length; i++) {
      if (cards[i].scaffolds.includes('fact')) {
        cardFacts[i] = keyFacts[pIdx] ?? vaultFact ?? null;
        pIdx++;
      } else {
        cardFacts[i] = null;
      }
    }

    const suggestedSeconds = (question.marks ?? 4) * 60;
    const timerColor = timerElapsed > suggestedSeconds * 2
      ? 'text-[#f85149]'
      : timerElapsed > suggestedSeconds
      ? 'text-[#d29922]'
      : 'text-[#484f58]';

    return (
      <div ref={containerRef} className="flex-1 flex flex-col h-full overflow-y-auto">
        {/* ── Sticky Header (question + progress) ─────────────────────────── */}
        <div className="px-6 pt-4 pb-3 bg-[#0d1117] sticky top-0 z-10 border-b border-[#30363d]"
             style={{ boxShadow: '0 4px 12px 0 rgba(13,17,23,0.85)' }}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full border border-[#30363d] text-[#8b949e]">
                  {question.topic?.replace(/_/g, ' ')}
                </span>
                {question.year && (
                  <span className="text-xs text-[#8b949e] opacity-60">{question.year}</span>
                )}
              </div>
              <p className="text-[#e6edf3] text-sm font-medium leading-relaxed">
                {questionText}
              </p>
            </div>

            {/* Archetype pill + top Mark button */}
            <div className="flex-shrink-0 flex flex-col items-end gap-2">
              <div className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg border ${archetypeDef.tw.border} ${archetypeDef.tw.bg}`}>
                <span className={`text-xl font-bold font-mono ${archetypeDef.tw.text}`}>{question.marks}</span>
                <span className={`${archetypeDef.tw.text} opacity-60 text-[9px] font-mono`}>{archetypeDef.label}</span>
              </div>
              <button
                onClick={onSubmit}
                disabled={isLoading || !allCardsFilled}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#58a6ff] text-[#0d1117] rounded-lg text-xs font-semibold hover:bg-[#79c0ff] transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
              >
                {isLoading ? (
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {isLoading ? 'Marking…' : 'Mark'}
              </button>
            </div>
          </div>

          {/* Progress dots + timer */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex gap-1">
              {cardLabels.map((lbl) => (
                <div
                  key={lbl}
                  className="h-1 w-8 rounded-full transition-all"
                  style={{ backgroundColor: steps[lbl]?.trim() ? '#64748b' : '#30363d' }}
                />
              ))}
            </div>
            <span className="text-[#8b949e] text-xs">
              {filledCount}/{cardLabels.length} cards · {totalWords} words
            </span>
            {(timerActive || timerElapsed > 0) && (
              <span className={`ml-auto text-[10px] font-mono tabular-nums ${timerColor}`}>
                {formatTime(timerElapsed)}
              </span>
            )}
          </div>
        </div>

        {/* ── Cards (scrolling) ────────────────────────────────────────────── */}
        <div className="px-6 py-4 space-y-4">

          {/* Question-level hints */}
          {(csHint || briefExamples.length > 0) && (
            <div className="space-y-2">
              {/* Unrevealed buttons — all on one line */}
              {(
                (csHint && !revealedHints['__cs__']?.caseStudy) ||
                briefExamples.some((_, i) => !revealedHints[`__ex_${i}__`]?.example)
              ) && (
                <div className="flex flex-wrap gap-2">
                  {csHint && !revealedHints['__cs__']?.caseStudy && (
                    <button
                      onClick={() => handleRevealHint('__cs__', 'caseStudy')}
                      className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono hover:border-[#58a6ff] hover:text-[#58a6ff] transition-colors"
                    >
                      [? Study/Example]
                    </button>
                  )}
                  {briefExamples.map((_, i) => (
                    !revealedHints[`__ex_${i}__`]?.example && (
                      <button
                        key={i}
                        onClick={() => handleRevealHint(`__ex_${i}__`, 'example')}
                        className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono hover:border-[#bc8cff] hover:text-[#bc8cff] transition-colors"
                      >
                        [? Example {i + 1}]
                      </button>
                    )
                  ))}
                </div>
              )}
              {/* Revealed panels — each shown as a full-width block below */}
              {csHint && revealedHints['__cs__']?.caseStudy && (
                <div className="text-[10px] font-mono bg-[#161b22] rounded px-2.5 py-1.5 leading-relaxed border border-[#30363d]">
                  <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                  <span className="text-[#e6edf3]">reveal_case_study</span>
                  <br />
                  <span className="text-[#3fb950]">&gt; {csHint}</span>
                </div>
              )}
              {briefExamples.map((ex, i) => (
                revealedHints[`__ex_${i}__`]?.example && (
                  <div key={i} className="text-[10px] font-mono bg-[#161b22] rounded px-2.5 py-1.5 leading-relaxed border border-[#30363d]">
                    <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                    <span className="text-[#e6edf3]">reveal_example --n={i + 1}</span>
                    <br />
                    <span className="text-[#bc8cff]">&gt; {ex}</span>
                  </div>
                )
              ))}
            </div>
          )}
          {cards.map((card, index) => {
            const value   = steps[card.label] || '';
            const wc      = wordCount(value);
            const hints   = revealedHints[card.label] || {};
            const fact    = cardFacts[index];
            const hasFact = card.scaffolds.includes('fact') && fact !== null;

            // Word-count colour
            let wcColor = 'text-[#8b949e]';
            if (wc > card.maxWords)        wcColor = 'text-[#f85149]';
            else if (wc >= card.targetWords) wcColor = 'text-[#d29922]';

            return (
              <div
                key={card.label}
                className="rounded-lg border bg-[#161b22] border-[#30363d] transition-all"
              >
                {/* Card header */}
                <div className="px-4 pt-3 pb-1 flex items-start justify-between gap-2">
                  <div>
                    <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-slate-500/10 text-slate-400 font-mono">
                      {card.label}
                    </span>
                    <p className="text-[#8b949e] text-[10px] mt-1 leading-relaxed">
                      {card.purpose}
                    </p>
                  </div>
                  {/* Live word count */}
                  <span className={`text-[10px] font-mono flex-shrink-0 mt-0.5 ${wcColor}`}>
                    {wc} / {card.maxWords} words
                  </span>
                </div>

                {/* Scaffold buttons */}
                <div className="px-4 pb-2 flex items-center gap-2 flex-wrap">
                  {hasFact && (
                    <button
                      onClick={() => handleRevealHint(card.label, 'fact')}
                      disabled={hints.fact || isLoading}
                      className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono hover:border-[#d29922] hover:text-[#d29922] transition-colors disabled:opacity-40 disabled:cursor-default"
                    >
                      [? Fact]
                    </button>
                  )}
                  {!hasFact && card.scaffolds.includes('fact') && (
                    <button
                      disabled
                      className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono opacity-40 cursor-default"
                    >
                      [? Fact]
                    </button>
                  )}
                </div>

                {/* Revealed hint panels */}
                {hints.fact && (
                  <div className="px-4 pb-2 space-y-1.5">
                    {hints.fact && fact && (
                      <div className="text-[10px] font-mono bg-[#0d1117] rounded px-2.5 py-1.5 leading-relaxed">
                        <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                        <span className="text-[#e6edf3]">reveal_evidence</span>
                        <br />
                        <span className="text-[#d29922]">&gt; {fact}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Textarea */}
                <div className="px-4 pb-1">
                  <textarea
                    ref={(el) => (refs.current[index] = el)}
                    value={value}
                    onChange={(e) => onChange(card.label, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index, cards.length)}
                    placeholder={`Write your ${card.label.toLowerCase()} here…`}
                    rows={card.rows}
                    disabled={isLoading}
                    className="w-full bg-transparent text-sm text-[#e6edf3] placeholder-[#30363d] resize-none outline-none leading-relaxed pb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            );
          })}

          {/* Submit row */}
          <div className="flex items-center justify-between pt-2 pb-6">
            <div className="flex items-center gap-3">
              <p className="text-[#8b949e] text-xs">
                <kbd className="px-1.5 py-0.5 rounded border border-[#30363d] text-[#8b949e] text-[10px]">
                  Tab
                </kbd>{' '}
                cycles cards
              </p>
              {onClearAll && (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  disabled={isLoading}
                  className="text-xs text-[#8b949e] hover:text-[#f85149] border border-transparent hover:border-[#f85149]/30 px-2 py-1 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Clear All
                </button>
              )}
            </div>
            <button
              onClick={onSubmit}
              disabled={isLoading || !allCardsFilled}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#58a6ff] text-[#0d1117] rounded-lg text-sm font-semibold hover:bg-[#79c0ff] transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Marking…
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Mark My Answer
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Clear All Confirmation Dialog ─────────────────────────────────── */}
        {showClearConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d1117]/80 backdrop-blur-sm">
            <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
              <h3 className="text-[#e6edf3] text-sm font-semibold mb-2">Clear all cards?</h3>
              <p className="text-[#8b949e] text-xs leading-relaxed mb-5">
                This will reset all your current un-marked drafts. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="px-4 py-2 rounded-lg border border-[#30363d] text-[#8b949e] text-xs hover:text-[#e6edf3] hover:border-[#8b949e] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setShowClearConfirm(false); onClearAll(); }}
                  className="px-4 py-2 rounded-lg bg-[#f85149]/10 border border-[#f85149]/40 text-[#f85149] text-xs font-semibold hover:bg-[#f85149]/20 transition-colors"
                >
                  Yes, Clear All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── ESSAY_20 LAYOUT ─────────────────────────────────────────────────────
  if (archetype === 'essay_20') {
    const archetypeDef = ARCHETYPES['essay_20'];
    const cards        = archetypeDef.cards;
    const cardLabels   = cards.map((c) => c.label);

    const allCardsFilled = cardLabels.every((lbl) => steps[lbl]?.trim().length > 0);
    const filledCount    = cardLabels.filter((lbl) => steps[lbl]?.trim()).length;
    const totalWords     = cardLabels.reduce((acc, lbl) => acc + wordCount(steps[lbl]), 0);

    const keyFacts = question.key_facts ?? [];

    const suggestedSeconds = 20 * 60;
    const timerColor = timerElapsed > suggestedSeconds * 2
      ? 'text-[#f85149]'
      : timerElapsed > suggestedSeconds
      ? 'text-[#d29922]'
      : 'text-[#484f58]';

    return (
      <div ref={containerRef} className="flex-1 flex flex-col h-full overflow-y-auto">

        {/* ── Sticky Header ────────────────────────────────────────────────── */}
        <div className="px-6 pt-4 pb-3 bg-[#0d1117] sticky top-0 z-10 border-b border-[#30363d]"
             style={{ boxShadow: '0 4px 12px 0 rgba(13,17,23,0.85)' }}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full border border-[#30363d] text-[#8b949e]">
                  {question.topic?.replace(/_/g, ' ')}
                </span>
                {question.year && (
                  <span className="text-xs text-[#8b949e] opacity-60">{question.year}</span>
                )}
                {question.is_synoptic && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-rose-500/30 bg-rose-500/10 text-rose-400 font-mono">
                    SYNOPTIC
                  </span>
                )}
              </div>
              <p className="text-[#e6edf3] text-sm font-medium leading-relaxed">
                {questionText}
              </p>
            </div>

            {/* Archetype pill + top Mark button */}
            <div className="flex-shrink-0 flex flex-col items-end gap-2">
              <div className="flex flex-col items-center justify-center px-3 py-2 rounded-lg border border-rose-500/40 bg-rose-500/10">
                <span className="text-xl font-bold font-mono text-rose-400">{question.marks}</span>
                <span className="text-rose-400 opacity-60 text-[9px] font-mono">ESSAY</span>
              </div>
              <button
                onClick={onSubmit}
                disabled={isLoading || !allCardsFilled}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#58a6ff] text-[#0d1117] rounded-lg text-xs font-semibold hover:bg-[#79c0ff] transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
              >
                {isLoading ? (
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {isLoading ? 'Marking…' : 'Mark'}
              </button>
            </div>
          </div>

          {/* Progress dots + word count + timer */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex gap-0.5">
              {cardLabels.map((lbl) => (
                <div
                  key={lbl}
                  className="h-1 w-5 rounded-full transition-all"
                  style={{ backgroundColor: steps[lbl]?.trim() ? '#f43f5e' : '#30363d' }}
                />
              ))}
            </div>
            <span className="text-[#8b949e] text-xs">
              {filledCount}/{cardLabels.length} cards · {totalWords} words
            </span>
            {(timerActive || timerElapsed > 0) && (
              <span className={`ml-auto text-[10px] font-mono tabular-nums ${timerColor}`}>
                {formatTime(timerElapsed)}
              </span>
            )}
          </div>
        </div>

        {/* ── Hints row ────────────────────────────────────────────────────── */}
        {(csHint || keyFacts.length > 0) && (
          <div className="px-6 pt-4 space-y-2">
            {(
              (csHint && !revealedHints['__cs__']?.caseStudy) ||
              keyFacts.some((_, i) => !revealedHints[`__fact_${i}__`]?.fact)
            ) && (
              <div className="flex flex-wrap gap-2">
                {csHint && !revealedHints['__cs__']?.caseStudy && (
                  <button
                    onClick={() => handleRevealHint('__cs__', 'caseStudy')}
                    className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono hover:border-[#58a6ff] hover:text-[#58a6ff] transition-colors"
                  >
                    [? Case Study]
                  </button>
                )}
                {keyFacts.map((_, i) => (
                  !revealedHints[`__fact_${i}__`]?.fact && (
                    <button
                      key={i}
                      onClick={() => handleRevealHint(`__fact_${i}__`, 'fact')}
                      className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono hover:border-[#d29922] hover:text-[#d29922] transition-colors"
                    >
                      [? Key Fact {i + 1}]
                    </button>
                  )
                ))}
              </div>
            )}
            {csHint && revealedHints['__cs__']?.caseStudy && (
              <div className="text-[10px] font-mono bg-[#161b22] rounded px-2.5 py-1.5 leading-relaxed border border-[#30363d]">
                <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                <span className="text-[#e6edf3]">reveal_case_study</span>
                <br />
                <span className="text-[#3fb950]">&gt; {csHint}</span>
              </div>
            )}
            {keyFacts.map((fact, i) => (
              revealedHints[`__fact_${i}__`]?.fact && (
                <div key={i} className="text-[10px] font-mono bg-[#161b22] rounded px-2.5 py-1.5 leading-relaxed border border-[#30363d]">
                  <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                  <span className="text-[#e6edf3]">reveal_key_fact --n={i + 1}</span>
                  <br />
                  <span className="text-[#d29922]">&gt; {fact}</span>
                </div>
              )
            ))}
          </div>
        )}

        {/* ── Cards (scrolling) ────────────────────────────────────────────── */}
        <div className="px-6 py-4 space-y-4">
          {cards.map((card, index) => {
            const value = steps[card.label] || '';
            const wc    = wordCount(value);

            let wcColor = 'text-[#8b949e]';
            if (wc > card.maxWords)          wcColor = 'text-[#f85149]';
            else if (wc >= card.targetWords) wcColor = 'text-[#d29922]';

            const isConclusion = card.label === 'EVALUATIVE CONCLUSION';
            const accentColor  = isConclusion ? '#f43f5e' : '#f87171';

            return (
              <div
                key={card.label}
                className="rounded-lg border bg-[#161b22] border-[#30363d] transition-all"
                style={{ borderLeftWidth: '2px', borderLeftColor: accentColor }}
              >
                {/* Card header */}
                <div className="px-4 pt-3 pb-1 flex items-start justify-between gap-2">
                  <div>
                    <span className="text-xs font-semibold text-rose-400/80 font-mono">
                      {card.label}
                    </span>
                    <p className="text-[#8b949e] text-[10px] mt-1 leading-relaxed">
                      {card.purpose}
                    </p>
                  </div>
                  <span className={`text-[10px] font-mono flex-shrink-0 mt-0.5 ${wcColor}`}>
                    {wc} / {card.maxWords} words
                  </span>
                </div>

                {/* Sentence starters (Guided Mode only) */}
                {guidedMode && card.starters?.length > 0 && (
                  <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                    {card.starters.map((starter) => (
                      <button
                        key={starter}
                        onClick={() => onChange(card.label, value ? value + ' ' + starter : starter)}
                        disabled={isLoading}
                        className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono hover:border-rose-500/40 hover:text-rose-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {starter}
                      </button>
                    ))}
                  </div>
                )}

                {/* Textarea */}
                <div className="px-4 pb-1">
                  <textarea
                    ref={(el) => (refs.current[index] = el)}
                    value={value}
                    onChange={(e) => onChange(card.label, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index, cards.length)}
                    placeholder={`Write your ${card.label.toLowerCase()} here…`}
                    rows={card.rows}
                    disabled={isLoading}
                    className="w-full bg-transparent text-sm text-[#e6edf3] placeholder-[#30363d] resize-none outline-none leading-relaxed pb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            );
          })}

          {/* ── Bottom action row ──────────────────────────────────────────── */}
          <div className="flex items-center justify-between pt-2 pb-6">
            <div className="flex items-center gap-3">
              <p className="text-[#8b949e] text-xs">
                <kbd className="px-1.5 py-0.5 rounded border border-[#30363d] text-[#8b949e] text-[10px]">Tab</kbd>
                {' '}cycles cards
              </p>
              {onClearAll && (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  disabled={isLoading}
                  className="text-xs text-[#8b949e] hover:text-[#f85149] border border-transparent hover:border-[#f85149]/30 px-2 py-1 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Clear All
                </button>
              )}
            </div>
            <button
              onClick={onSubmit}
              disabled={isLoading || !allCardsFilled}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#58a6ff] text-[#0d1117] rounded-lg text-sm font-semibold hover:bg-[#79c0ff] transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Marking…
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Mark My Answer
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Clear All Confirmation ────────────────────────────────────────── */}
        {showClearConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d1117]/80 backdrop-blur-sm">
            <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
              <h3 className="text-[#e6edf3] text-sm font-semibold mb-2">Clear all cards?</h3>
              <p className="text-[#8b949e] text-xs leading-relaxed mb-5">
                This will reset all your current un-marked drafts. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="px-4 py-2 rounded-lg border border-[#30363d] text-[#8b949e] text-xs hover:text-[#e6edf3] hover:border-[#8b949e] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setShowClearConfirm(false); onClearAll(); }}
                  className="px-4 py-2 rounded-lg bg-[#f85149]/10 border border-[#f85149]/40 text-[#f85149] text-xs font-semibold hover:bg-[#f85149]/20 transition-colors"
                >
                  Yes, Clear All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── SCAFFOLD_9 / ENVSCI_9 / ENVSCI_25 LAYOUT ────────────────────────────
  if (archetype === 'scaffold_9' || archetype === 'envsci_9' || archetype === 'envsci_25') {
    const archetypeDef = ARCHETYPES[archetype];
    const cards        = archetypeDef.cards;
    const cardLabels   = cards.map((c) => c.label);

    const allCardsFilled = cardLabels.every((lbl) => steps[lbl]?.trim().length > 0);
    const filledCount    = cardLabels.filter((lbl) => steps[lbl]?.trim()).length;
    const totalWords     = cardLabels.reduce((acc, lbl) => acc + wordCount(steps[lbl]), 0);

    const phaseLabels = { 1: 'Introduction', 2: 'Main Argument', 3: 'Counter Argument', 4: 'Conclusion' };

    // Essay synthesis: cards 0-3 → P1, cards 4-7 → P2, card 8 → P3

    const suggestedSeconds = (archetypeDef.marks ?? 9) * 60;
    const timerColor = timerElapsed > suggestedSeconds * 2
      ? 'text-[#f85149]'
      : timerElapsed > suggestedSeconds
      ? 'text-[#d29922]'
      : 'text-[#484f58]';

    return (
      <div ref={containerRef} className="flex-1 flex flex-col h-full overflow-y-auto">

        {/* ── Sticky Header ────────────────────────────────────────────────── */}
        <div className="px-6 pt-4 pb-3 bg-[#0d1117] sticky top-0 z-10 border-b border-[#30363d]"
             style={{ boxShadow: '0 4px 12px 0 rgba(13,17,23,0.85)' }}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full border border-[#30363d] text-[#8b949e]">
                  {question.topic?.replace(/_/g, ' ')}
                </span>
                {question.year && (
                  <span className="text-xs text-[#8b949e] opacity-60">{question.year}</span>
                )}
              </div>
              <p className="text-[#e6edf3] text-sm font-medium leading-relaxed">
                {questionText}
              </p>
            </div>

            {/* Archetype pill + top Mark button */}
            <div className="flex-shrink-0 flex flex-col items-end gap-2">
              <div className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg border ${archetypeDef.tw.border} ${archetypeDef.tw.bg}`}>
                <span className={`text-xl font-bold font-mono ${archetypeDef.tw.text}`}>{question.marks}</span>
                <span className={`${archetypeDef.tw.text} opacity-60 text-[9px] font-mono`}>{archetypeDef.label}</span>
              </div>
              <button
                onClick={onSubmit}
                disabled={isLoading || !allCardsFilled}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#58a6ff] text-[#0d1117] rounded-lg text-xs font-semibold hover:bg-[#79c0ff] transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
              >
                {isLoading ? (
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {isLoading ? 'Marking…' : 'Mark'}
              </button>
            </div>
          </div>

          {/* Progress dots + word count + timer */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex gap-0.5">
              {cardLabels.map((lbl) => (
                <div
                  key={lbl}
                  className="h-1 w-4 rounded-full transition-all"
                  style={{ backgroundColor: steps[lbl]?.trim() ? archetypeDef.hex : '#30363d' }}
                />
              ))}
            </div>
            <span className="text-[#8b949e] text-xs">
              {filledCount}/{cardLabels.length} cards · {totalWords} words
            </span>
            {(timerActive || timerElapsed > 0) && (
              <span className={`ml-auto text-[10px] font-mono tabular-nums ${timerColor}`}>
                {formatTime(timerElapsed)}
              </span>
            )}
          </div>
        </div>

        {/* ── Cards (scrolling) ────────────────────────────────────────────── */}
        <div className="px-6 py-4 space-y-4">
          {cards.map((card, index) => {
            const value = steps[card.label] || '';
            const wc    = wordCount(value);

            // Word-count colour
            let wcColor = 'text-[#8b949e]';
            if (wc > card.maxWords)          wcColor = 'text-[#f85149]';
            else if (wc >= card.targetWords) wcColor = 'text-[#d29922]';

            // AO styling
            const isAO1       = card.aoType === 'ao1';
            const accentColor = isAO1 ? '#3fb950' : '#58a6ff';
            const aoBadge     = isAO1
              ? 'bg-[#3fb950]/10 text-[#3fb950] border border-[#3fb950]/30'
              : 'bg-[#58a6ff]/10 text-[#58a6ff] border border-[#58a6ff]/30';

            // Phase header (render before the first card of each new phase).
            // Skip entirely when the archetype's cards don't carry phase metadata.
            const showPhaseHeader = card.phase != null
              && (index === 0 || card.phase !== cards[index - 1].phase);

            return (
              <React.Fragment key={card.label}>
                {showPhaseHeader && (
                  <div className={`${index > 0 ? 'pt-4' : ''}`}>
                    <span className="text-[10px] font-semibold font-mono text-[#8b949e] uppercase tracking-widest">
                      ── Phase {card.phase}: {phaseLabels[card.phase]} ──
                    </span>
                  </div>
                )}

                <div
                  className="rounded-lg border bg-[#161b22] border-[#30363d] transition-all"
                  style={{ borderLeftWidth: '2px', borderLeftColor: accentColor }}
                >
                  {/* Card header */}
                  <div className="px-4 pt-3 pb-1 flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {card.aoType && (
                        <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${aoBadge}`}>
                          {isAO1 ? 'AO1' : 'AO2'}
                        </span>
                      )}
                      <span className="text-xs font-semibold text-[#8b949e] font-mono">
                        {card.label.replace(/^AO[12]: /, '')}
                      </span>
                    </div>
                    {/* Live word count */}
                    <span className={`text-[10px] font-mono flex-shrink-0 mt-0.5 ${wcColor}`}>
                      {wc} / {card.maxWords} words
                    </span>
                  </div>

                  {/* Purpose */}
                  <div className="px-4 pb-1">
                    <p className="text-[#8b949e] text-[10px] leading-relaxed">{card.purpose}</p>
                  </div>

                  {/* Sentence starters (Guided Mode only) */}
                  {guidedMode && card.starters?.length > 0 && (
                    <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                      {card.starters.map((starter) => (
                        <button
                          key={starter}
                          onClick={() =>
                            onChange(card.label, value ? value + ' ' + starter : starter)
                          }
                          disabled={isLoading}
                          className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono hover:border-[#58a6ff] hover:text-[#58a6ff] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {starter}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Textarea */}
                  <div className="px-4 pb-1">
                    <textarea
                      ref={(el) => (refs.current[index] = el)}
                      value={value}
                      onChange={(e) => onChange(card.label, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index, cards.length)}
                      placeholder={`Write your ${card.label.toLowerCase().replace(/^ao[12]: /, '')} here…`}
                      rows={card.rows}
                      disabled={isLoading}
                      className="w-full bg-transparent text-sm text-[#e6edf3] placeholder-[#30363d] resize-none outline-none leading-relaxed pb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* ── Bottom action row ──────────────────────────────────────────── */}
          <div className="flex items-center justify-between pt-2 pb-6">
            <div className="flex items-center gap-3">
              <p className="text-[#8b949e] text-xs">
                <kbd className="px-1.5 py-0.5 rounded border border-[#30363d] text-[#8b949e] text-[10px]">Tab</kbd>
                {' '}cycles cards
              </p>
              {onClearAll && (
                <button
                  onClick={() => setShowClearConfirm(true)}
                  disabled={isLoading}
                  className="text-xs text-[#8b949e] hover:text-[#f85149] border border-transparent hover:border-[#f85149]/30 px-2 py-1 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Clear All
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onSubmit}
                disabled={isLoading || !allCardsFilled}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#58a6ff] text-[#0d1117] rounded-lg text-sm font-semibold hover:bg-[#79c0ff] transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Marking…
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Mark My Answer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── Clear All Confirmation ────────────────────────────────────────── */}
        {showClearConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d1117]/80 backdrop-blur-sm">
            <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
              <h3 className="text-[#e6edf3] text-sm font-semibold mb-2">Clear all cards?</h3>
              <p className="text-[#8b949e] text-xs leading-relaxed mb-5">
                This will reset all your current un-marked drafts. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="px-4 py-2 rounded-lg border border-[#30363d] text-[#8b949e] text-xs hover:text-[#e6edf3] hover:border-[#8b949e] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setShowClearConfirm(false); onClearAll(); }}
                  className="px-4 py-2 rounded-lg bg-[#f85149]/10 border border-[#f85149]/40 text-[#f85149] text-xs font-semibold hover:bg-[#f85149]/20 transition-colors"
                >
                  Yes, Clear All
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }

  // ─── 6-MARK / 9-MARK LAYOUT (unchanged) ──────────────────────────────────

  const totalWords  = activeSteps.reduce((acc, s) => acc + wordCount(steps[s.id]), 0);
  const filledSteps = activeSteps.filter((s) => steps[s.id]?.trim()).length;
  const allRevealed = activeSteps.every((s) => revealedModelAnswers.has(s.id));

  const is6Mark     = markType === '6-mark';
  const badgeColor  = is6Mark ? 'text-[#58a6ff]' : 'text-[#d29922]';
  const badgeBorder = is6Mark ? 'border-[#58a6ff]/40' : 'border-[#d29922]/40';
  const badgeBg     = is6Mark ? 'bg-[#58a6ff]/10'    : 'bg-[#d29922]/10';

  return (
    <div ref={containerRef} className="flex-1 flex flex-col h-full overflow-y-auto">
      {/* ── Question Header ─────────────────────────────────────────────── */}
      <div className="px-6 py-4 border-b border-[#30363d] bg-[#0d1117] sticky top-0 z-10">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs px-2 py-0.5 rounded-full border border-[#30363d] text-[#8b949e]">
                {question.topic}
              </span>
              <span className="text-xs text-[#8b949e] opacity-60">{question.angle}</span>
            </div>
            <p className="text-[#e6edf3] text-sm font-medium leading-relaxed">
              {questionText}
            </p>
          </div>

          {/* Mark badge */}
          <div className={`flex-shrink-0 flex flex-col items-center justify-center w-14 h-14 rounded-lg border ${badgeBorder} ${badgeBg}`}>
            <span className={`text-xl font-bold font-mono ${badgeColor}`}>
              {question.marks}
            </span>
            <span className="text-[#8b949e] text-[9px] font-mono">marks</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 flex items-center gap-3">
          <div className="flex gap-1">
            {activeSteps.map((s, i) => (
              <div
                key={s.id}
                className="h-1 w-8 rounded-full transition-all"
                style={{
                  backgroundColor: steps[s.id]?.trim()
                    ? ['#58a6ff', '#3fb950', '#d29922', '#bc8cff'][i]
                    : '#30363d',
                }}
              />
            ))}
          </div>
          <span className="text-[#8b949e] text-xs">
            {filledSteps}/{activeSteps.length} steps · {totalWords} words
          </span>
        </div>
      </div>

      {/* ── Steps ───────────────────────────────────────────────────────── */}
      <div className="px-6 py-4 space-y-4">
        {activeSteps.map((step, index) => {
          const colors      = STEP_COLORS[index];
          const value       = steps[step.id] || '';
          const wc          = wordCount(value);
          const hints       = revealedHints[step.id] || {};
          const isRevealed  = revealedModelAnswers.has(step.id);
          const isAnimating = animatingSteps.has(step.id);
          const isPoint2    = step.id === 'point2';

          return (
            <div
              key={step.id}
              className={`rounded-lg border bg-[#161b22] transition-all ${
                isRevealed ? 'border-[#3fb950]/40' : 'border-[#30363d]'
              }`}
            >
              {/* Card header */}
              <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${colors.badge}`}>
                    {step.label}
                  </span>
                  <span className={`text-xs font-medium ${colors.label}`}>{step.title}</span>
                </div>
                <span className="text-[#8b949e] text-[10px]">{wc > 0 ? `${wc}w` : 'empty'}</span>
              </div>

              {/* Scaffold buttons */}
              <div className="px-4 pb-2 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => handleRevealHint(step.id, 'caseStudy')}
                  disabled={hints.caseStudy || isLoading}
                  className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono hover:border-[#58a6ff] hover:text-[#58a6ff] transition-colors disabled:opacity-40 disabled:cursor-default"
                >
                  [? Study/Example]
                </button>

                <button
                  onClick={() => handleRevealHint(step.id, 'fact')}
                  disabled={hints.fact || isLoading || !vaultFact}
                  className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono hover:border-[#d29922] hover:text-[#d29922] transition-colors disabled:opacity-40 disabled:cursor-default"
                >
                  [? Fact]
                </button>

                {/* [? Add Breadth] — Step 3 of 9-mark only */}
                {isPoint2 && (
                  <button
                    onClick={() => setBreadthIndex((prev) => Math.min(prev + 1, briefExamples.length))}
                    disabled={isLoading || breadthIndex >= briefExamples.length}
                    className="text-[10px] px-2 py-0.5 rounded border border-[#30363d] text-[#8b949e] font-mono hover:border-[#bc8cff] hover:text-[#bc8cff] transition-colors disabled:opacity-40 disabled:cursor-default"
                  >
                    [? Add Breadth]
                  </button>
                )}

                {/* [Run Model Answer] — gated until after marking */}
                <button
                  onClick={() => handleRunModelAnswer(step.id)}
                  disabled={isRevealed || isLoading || !hasBeenMarked}
                  title={!hasBeenMarked ? 'Submit your answer for marking first' : undefined}
                  className={`text-[10px] px-2 py-0.5 rounded border font-mono transition-colors ${
                    !hasBeenMarked
                      ? 'border-[#30363d] text-[#30363d] cursor-not-allowed'
                      : isRevealed
                      ? 'border-[#30363d] text-[#30363d] opacity-40 cursor-default'
                      : 'border-[#30363d] text-[#8b949e] hover:border-[#3fb950] hover:text-[#3fb950]'
                  }`}
                >
                  {!hasBeenMarked ? '[🔒 Run Model Answer]' : '[Run Model Answer]'}
                </button>
              </div>

              {/* Revealed hint panels */}
              {(hints.caseStudy || hints.fact || (isPoint2 && breadthIndex > 0)) && (
                <div className="px-4 pb-2 space-y-1.5">
                  {hints.caseStudy && (
                    <div className="text-[10px] font-mono bg-[#0d1117] rounded px-2.5 py-1.5 leading-relaxed">
                      <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                      <span className="text-[#e6edf3]">reveal_case_study</span>
                      <br />
                      <span className="text-[#3fb950]">&gt; {csHint}</span>
                    </div>
                  )}

                  {hints.fact && vaultFact && (
                    <div className="text-[10px] font-mono bg-[#0d1117] rounded px-2.5 py-1.5 leading-relaxed">
                      <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                      <span className="text-[#e6edf3]">reveal_evidence --vault="{majorCS}"</span>
                      <br />
                      <span className="text-[#3fb950]">&gt; {vaultFact}</span>
                    </div>
                  )}

                  {isPoint2 &&
                    briefExamples.slice(0, breadthIndex).map((ex, i) => (
                      <div
                        key={i}
                        className="text-[10px] font-mono bg-[#0d1117] rounded px-2.5 py-1.5 leading-relaxed"
                      >
                        <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
                        <span className="text-[#e6edf3]">reveal_breadth --example={i + 1}</span>
                        <br />
                        <span className="text-[#bc8cff]">&gt; {ex}</span>
                      </div>
                    ))}
                </div>
              )}

              {/* Textarea */}
              <div className="px-4 pb-1">
                <textarea
                  ref={(el) => (refs.current[index] = el)}
                  key={isAnimating ? `${step.id}-anim` : step.id}
                  value={value}
                  onChange={(e) => onChange(step.id, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index, activeSteps.length)}
                  placeholder={step.placeholder}
                  rows={step.rows}
                  disabled={isLoading}
                  className={`w-full bg-transparent text-sm placeholder-[#30363d] resize-none outline-none leading-relaxed pb-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                    isRevealed ? 'text-[#3fb950] animate-ghost-fade' : 'text-[#e6edf3]'
                  }`}
                />
              </div>
            </div>
          );
        })}

        {/* All-revealed encouragement */}
        {allRevealed && (
          <div className="rounded-lg border border-[#3fb950]/30 bg-[#3fb950]/5 px-4 py-3">
            <div className="text-[10px] font-mono leading-relaxed">
              <span className="text-[#8b949e]">user@tutor:~$</span>{' '}
              <span className="text-[#e6edf3]">analyse_structure --mode=A*</span>
              <br />
              <span className="text-[#3fb950] mt-1 block">
                &gt; This is what an A* structure looks like. Try to replicate this logic in the
                next question!
              </span>
            </div>
          </div>
        )}

        {/* Submit row */}
        <div className="flex items-center justify-between pt-2 pb-6">
          <div className="flex items-center gap-3">
            <p className="text-[#8b949e] text-xs">
              <kbd className="px-1.5 py-0.5 rounded border border-[#30363d] text-[#8b949e] text-[10px]">
                Tab
              </kbd>{' '}
              cycles steps
            </p>
            {onClearAll && (
              <button
                onClick={() => setShowClearConfirm(true)}
                disabled={isLoading}
                className="text-xs text-[#8b949e] hover:text-[#f85149] border border-transparent hover:border-[#f85149]/30 px-2 py-1 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Clear All
              </button>
            )}
          </div>
          <button
            onClick={onSubmit}
            disabled={isLoading || filledSteps === 0}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#58a6ff] text-[#0d1117] rounded-lg text-sm font-semibold hover:bg-[#79c0ff] transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Marking…
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Mark My Answer
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Clear All Confirmation Dialog ───────────────────────────────────── */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0d1117]/80 backdrop-blur-sm">
          <div className="bg-[#161b22] border border-[#30363d] rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="text-[#e6edf3] text-sm font-semibold mb-2">Clear all steps?</h3>
            <p className="text-[#8b949e] text-xs leading-relaxed mb-5">
              This will reset your current un-marked drafts. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="px-4 py-2 rounded-lg border border-[#30363d] text-[#8b949e] text-xs hover:text-[#e6edf3] hover:border-[#8b949e] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowClearConfirm(false); onClearAll(); }}
                className="px-4 py-2 rounded-lg bg-[#f85149]/10 border border-[#f85149]/40 text-[#f85149] text-xs font-semibold hover:bg-[#f85149]/20 transition-colors"
              >
                Yes, Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
